import { z } from 'zod';

import { APP_CONFIG } from '@/config/env';
import { formatFileSize } from '@/utils/file';
import { ALLOW_AUDIO_EXT, ALLOW_PICTURE_EXT, ALLOW_VIDEO_EXT } from './constants';

export type CBArgsType = {
  clientId: string;
  userId: string;
  orgId: string;
  originalName: string;
  fileBizType:
    | 'asset_center_material_upload_image_file'
    | 'asset_center_material_upload_video_file'
    | 'asset_center_material_upload_audio_file'
};

export const getCallbackArgs = (args: CBArgsType) => {
  return JSON.stringify({
    callbackUrl:
      (APP_CONFIG.isDev
        ? 'https://business.lightxads.com/api'
        : import.meta.env.VITE_API_BASE_URL) + '/resource/volcengine/callback/vod',
    callbackBodyType: 'application/json',
    callbackBody: JSON.stringify(args),
  });
};

export const getUploadType = (fileType: string) => {
  const ext = fileType.split('/').pop() || '';
  if (ALLOW_PICTURE_EXT.includes(ext)) {
    return 'image';
  }
  if (ALLOW_VIDEO_EXT.includes(ext)) {
    return 'video';
  }
  if (ALLOW_AUDIO_EXT.includes(ext)) {
    return 'audio';
  }
};

/**
 * 判断 TTUploader 是否因 STS 临时凭证过期而失败
 * 根据 SDK 返回的错误码 100026 识别凭证过期，避免依赖错误文案
 */
export const isStsTokenExpiredError = (errorCode?: number) => {
  return errorCode === 100026;
};

/**
 * 素材选择弹窗上传类型限制：1-视频，2-图片，3-音频
 * 未传限制时保持原有行为（视频/图片/音频均允许）
 */
export const isAllowedMaterialUpload = (
  uploadType: string | undefined,
  allowedMaterialType?: 1 | 2 | 3
) => {
  if (!allowedMaterialType) return true;
  if (allowedMaterialType === 1) return uploadType === 'video';
  if (allowedMaterialType === 2) return uploadType === 'image';
  if (allowedMaterialType === 3) return uploadType === 'audio';
  return true;
};

/**
 * 素材选择弹窗上传文件后缀限制（如音频只允许 mp3，投放仅支持 mp3）
 * 未传限制时保持原有行为（不限制后缀）
 */
export const isAllowedMaterialFileExt = (ext: string, allowedFileExts?: string[]) => {
  if (!allowedFileExts?.length) return true;
  return allowedFileExts.includes((ext || '').toLowerCase());
};

/**
 * 取单个文件大小上限：命中「按后缀区分的上限」时优先，否则用默认上限
 * 用于「mp3 单文件不超过 10MB」这类只针对特定格式的收紧限制
 */
export const getFileSizeLimitByExt = (
  ext: string,
  defaultMaxFileSize: number,
  maxFileSizeByExt?: Record<string, number>
) => maxFileSizeByExt?.[(ext || '').toLowerCase()] ?? defaultMaxFileSize;

/**
 * 生成「按后缀区分的单文件大小上限」提示文案，如 ['MP3 格式单个文件不超过 10 MB']
 */
export const buildFileSizeLimitHints = (maxFileSizeByExt?: Record<string, number>) =>
  Object.entries(maxFileSizeByExt ?? {}).map(
    ([ext, size]) => `${ext.toUpperCase()} 格式单个文件不超过 ${formatFileSize(size)}`
  );

/** 单个已上传素材的信息结构 */
export const uploadInfoSchema = z.object({
  ossKey: z.string(),
  md5: z.string().optional(),
  width: z.string().optional(),
  height: z.string().optional(),
  duration: z.number().optional(),
});

/**
 * 宽高归一为数字。
 * 上传回调给出的宽高可能是字符串（如 '1080'），而 `/material/save` 的契约要求数字。
 */
const toOptionalNumber = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * 组装 `/material/save` 的 infos 入参。
 *
 * `SaveMaterialInfo` 已移除 `resolution` 字符串，改为 `width` / `height` 数字。
 * 同步上传与异步上传共用本函数，避免异步链路继续下发 resolution
 * 导致上传完成后素材宽高没有落库。
 */
export const buildSaveMaterialInfo = (info: {
  ossKey: string;
  md5?: string;
  width?: string | number | null;
  height?: string | number | null;
  duration?: number;
}) => ({
  ossKey: info.ossKey,
  md5: info.md5,
  width: toOptionalNumber(info.width),
  height: toOptionalNumber(info.height),
  duration: info.duration,
});

/** 设计师 / 创意人跨字段校验：两者均非必填，但至少填一个 */
const hasDesignerOrCreative = (data: { designerId?: string; creativeId?: string }) =>
  Boolean(data.designerId) || Boolean(data.creativeId);

const AT_LEAST_ONE_ASSIGNEE = {
  message: '设计师与创意人至少选择一个',
  path: ['designerId'],
};

/** 上传表单基础结构（不含跨字段校验），异步模式在其上放宽 infos */
const baseMaterialUploadFormSchema = z.object({
  /** 设计师（非必填，与 creativeId 至少填一个） */
  designerId: z.string().optional(),
  /** 创意人（非必填，与 designerId 至少填一个） */
  creativeId: z.string().optional(),
  folderId: z.string().min(1, '请选择文件夹'),
  infos: z.array(uploadInfoSchema).min(1, '请上传文件'),
  tagIds: z.array(z.string()).optional(),
});

/** 同步模式下的表单结构，同时也是异步模式的扩展基础 */
export const materialUploadFormSchema = baseMaterialUploadFormSchema.refine(
  hasDesignerOrCreative,
  AT_LEAST_ONE_ASSIGNEE
);

/**
 * 上传素材抽屉的表单校验规则。
 *
 * 同步模式下文件在抽屉内上传，提交时 `infos` 必须有值；
 * 异步（后台）模式下文件要等提交后才开始上传，提交瞬间 `infos` 必然为空，
 * 此时不能再用 `.min(1)` 卡住提交，改由抽屉的 `hasAsyncFiles` 控制按钮可用性。
 */
export const buildMaterialUploadFormSchema = (asyncMode: boolean) =>
  asyncMode
    ? baseMaterialUploadFormSchema
        .extend({ infos: z.array(uploadInfoSchema).optional() })
        .refine(hasDesignerOrCreative, AT_LEAST_ONE_ASSIGNEE)
    : materialUploadFormSchema;
